import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import blogData from "../config/blogPage";
import { Card } from "../components/card/Card";
import Image from "../components/image/Image";
import Heading from "../components/heading/Heading";
import { Paragraph } from "../components/paragraph/Paragraph";

const BlogDetailsPage = () => {
  const { slug } = useParams();
  const post = blogData.blogSection.posts.find((p) => p.slug === slug);
  const navigate = useNavigate();

  if (!post) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <Heading level={1} className="text-4xl font-bold mb-4">
          Post Not Found
        </Heading>
        <Paragraph className="mb-6 text-lg">
          The blog post you're looking for doesn't exist or may have been
          removed.
        </Paragraph>
        <Link
          to="/glowtech-blogs"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          Browse All Posts
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 mt-24">
      {/* Article Header */}
      <header className="mb-12">
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <span>{post.date}</span>
          {/* <span className="mx-2">•</span>
          <span>{post.readTime} read</span>
          <span className="mx-2">•</span>
          <span>{post.views} views</span> */}
        </div>

        <Heading level={1} className="text-4xl font-bold mb-6">
          {post.title}
        </Heading>

        <p className="text-xl text-gray-600 mb-8">{post.subtitle}</p>

        <div className="flex items-center mb-8">
          <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
            <Image
              src={post.author.avatar}
              alt={post.author.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="font-medium">{post.author.name}</p>
            <p className="text-gray-500">{post.source}</p>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      <div className="mb-12 rounded-xl overflow-hidden shadow-lg">
        <Image src={post.image} alt={post.title} className="w-full h-auto" />
      </div>

      {/* Article Content */}
      <article className="prose prose-lg max-w-none">
        <p className="text-xl font-medium mb-8">{post.context}</p>

        {post.description.map((paragraph, index) => (
          <p key={index} className="mb-6 leading-relaxed">
            {paragraph}
          </p>
        ))}

        {post.sections?.map((section, index) => (
          <section key={index} className="mb-12">
            <Heading level={2} className="text-2xl font-bold mb-6">
              {section.title}
            </Heading>
            <p className="mb-6">{section.content}</p>
            {section.image && (
              <div className="my-8">
                <Image
                  src={section.image}
                  alt={section.title}
                  className="w-full rounded-lg shadow-md"
                />
                {section.imageCaption && (
                  <p className="text-sm text-gray-500 mt-2 text-center">
                    {section.imageCaption}
                  </p>
                )}
              </div>
            )}
          </section>
        ))}
      </article>

      {/* Topics */}
      <div className="my-12">
        <div className="flex flex-wrap gap-2">
          {post.topics.map((topic, i) => (
            <span
              key={i}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium"
            >
              {topic}
            </span>
          ))}
        </div>
      </div>

      {/* Author Bio */}
      <div className="bg-gray-50 rounded-xl p-8 my-12">
        <div className="flex items-center">
          <div className="w-20 h-20 rounded-full overflow-hidden mr-6">
            <Image
              src={post.author.avatar}
              alt={post.author.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <Heading level={3} className="text-xl font-bold mb-2">
              About {post.author.name}
            </Heading>
            <p className="text-gray-700 mb-4">{post.author.bio}</p>
            <a
              href={post.author.linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800"
            >
              <span>Connect on LinkedIn</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="w-5 h-5 ml-1"
              >
                <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
      <button
        onClick={() => navigate(-1)}
        className="mb-6 text-blue-600 hover:underline flex items-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5 mr-1"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5L8.25 12l7.5-7.5"
          />
        </svg>
        Back to Blog Page
      </button>
    </div>
  );
};

export default BlogDetailsPage;
